import React, { useState, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import AuthForm from 'components/auth/AuthForm';
import useViewer from 'hooks/useViewer';

export default function AuthSubscribeSlideout({
  showHeading = true,
  heading = '',
  bottomText = '',
  showImmediately = false,
  delay = 5000,
}: {
  showHeading?: boolean;
  heading?: string;
  bottomText?: string;
  showImmediately?: boolean;
  delay?: number;
}) {
  const { viewer } = useViewer();
  const isAnonymous = !viewer || viewer.isAnonymous;

  const [isShowing, setIsShowing] = useState(false);
  const [hasWaited, setHasWaited] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const shouldSuppressSlideout = () => {
    try {
      const lastClosed = localStorage.getItem('slideout-last-closed');
      const hasSubmitted = localStorage.getItem('slideout-form-submitted');

      if (hasSubmitted === 'true') {
        return true;
      }

      if (lastClosed) {
        const lastClosedTime = parseInt(lastClosed);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        if (now - lastClosedTime < twentyFourHours) {
          return true;
        }
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    if (viewer !== undefined) {
      setAuthChecked(true);
    }
  }, [viewer]);

  useEffect(() => {
    if (!authChecked) return;

    if (!isAnonymous) return;

    if (shouldSuppressSlideout()) return;

    const timer = setTimeout(() => {
      setHasWaited(true);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [authChecked, isAnonymous, delay]);

  useEffect(() => {
    if (showImmediately && authChecked && isAnonymous && !shouldSuppressSlideout()) {
      setIsShowing(true);
    } else if (hasWaited && authChecked && isAnonymous && !shouldSuppressSlideout()) {
      setIsShowing(true);
    }
  }, [hasWaited, authChecked, isAnonymous, showImmediately]);

  const handleClose = () => {
    setIsShowing(false);

    try {
      localStorage.setItem('slideout-last-closed', Date.now().toString());
    } catch (error) {
      console.warn('Could not save slideout close time to localStorage');
    }
  };

  const handleFormSubmit = () => {
    try {
      localStorage.setItem('slideout-form-submitted', 'true');
    } catch (error) {
      console.warn('Could not save form submission status to localStorage');
    }
    setTimeout(() => {
      setIsShowing(false);
    }, 3000);
  };

  if (!authChecked || !isAnonymous || shouldSuppressSlideout()) {
    return null;
  }

  return (
    <Transition.Root show={isShowing} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="transition ease-in-out duration-500 transform"
        enterFrom="translate-x-full opacity-0"
        enterTo="translate-x-0 opacity-100"
        leave="transition ease-in-out duration-300 transform"
        leaveFrom="translate-x-0 opacity-100"
        leaveTo="translate-x-full opacity-0"
      >
        <div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border border-brand-primary bg-palette-black-3 shadow-[0_0_10px_rgba(0,122,255,0.5)]">
          <div className="relative overflow-hidden p-6">
            <button
              onClick={handleClose}
              className="absolute right-1 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-gray-400 transition-all duration-200 hover:bg-gray-700 hover:text-white"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="mb-4 text-center">
              <div className="mb-2 text-sm font-semibold uppercase text-brand-green-primary">
                AI Analysis | Trending News | Top Trades
              </div>
              <h2 className="styled-font text-xl font-bold text-white sm:text-2xl">
                Free Options Flow Insights.
                <br />
                Delivered.
              </h2>
            </div>

            {showHeading && (
              <div className="mb-6 text-center">
                <h3 className="font-space text-lg font-bold text-white sm:text-xl">{heading}</h3>
                <div className="border-gradient mx-auto mt-2 h-0.5 w-16 bg-gradient-to-r from-blue-500 to-teal-500"></div>
              </div>
            )}

            <div className="text-center">
              <p className="mt-2 text-sm font-medium leading-6 text-white">
                Add your email to receive our free daily newsletter. No spam, unsubscribe anytime.
              </p>
            </div>

            <div className="mt-6">
              <AuthForm onSubmitSuccess={handleFormSubmit} />
            </div>

            <div className="mt-4 text-center text-xs text-gray-300">{bottomText}</div>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>
  );
}
